/**
 * The details section of the booked-shipment page, and the seven dialogs that
 * modify it after booking — pickup date, delivery hours (not offered on
 * appointment deliveries), broker, both addresses, both contacts, and both
 * sets of references and notes.
 *
 * The section itself is presentation; what this file adds is which dialog each
 * "modify" affordance opens and whether the shipment needs reloading after.
 */

import { describeHours } from "@/Helpers/describeHours";
import type { Shipment } from "@freightsimple/generated-dashboard-openapi-client/models/Shipment";
import type { TrackingInfo } from "@freightsimple/generated-dashboard-openapi-client/models/TrackingInfo";
import { LocationContext } from "@freightsimple/generated-dashboard-openapi-client/models/LocationContext";
import moment from "moment";
import { useState } from "react";
import { ChangeDeliveryHoursDialog } from "@/Features/ChangeDeliveryHours/Exports/Dialogs/ChangeDeliveryHoursDialog";
import ViewShipmentsDetailsSection from "../CustomComponents/ViewShipmentsDetailsSection";
import { shipmentIsDeliveringByAppointment } from "@/Shared/Shipment/Helpers/shipmentIsDeliveringByAppointment";
import ViewShipmentsModifyContactAfterBookingDialog from "../Dialogs/ViewShipmentsModifyContactAfterBookingDialog";
import { useViewShipmentsChangeBrokerAfterBookingDialog } from "../Hooks/useViewShipmentsChangeBrokerAfterBookingDialog";
import { useViewShipmentsChangePickupDateDialog } from "../Hooks/useViewShipmentsChangePickupDateDialog";
import { useViewShipmentsModifyAddressAfterBookingDialog } from "../Hooks/useViewShipmentsModifyAddressAfterBookingDialog";
import { useViewShipmentsModifyReferenceAndNotesAfterBookingDialog } from "../Hooks/useViewShipmentsModifyReferenceAndNotesAfterBookingDialog";

interface ViewShipmentsDetailsSectionForShipmentProps {
  shipment: Shipment;
  trackingInfo: TrackingInfo | undefined;
  onReload: () => Promise<void>;
}

export function ViewShipmentsDetailsSectionForShipment(
  props: ViewShipmentsDetailsSectionForShipmentProps,
) {
  const { shipment } = props;
  const quote = shipment.selectedQuote;
  const [changeDeliveryHoursOpen, setChangeDeliveryHoursOpen] = useState(false);
  const changePickupDateDialog = useViewShipmentsChangePickupDateDialog(
    shipment,
    props.onReload,
  );
  const changeBrokerDialog = useViewShipmentsChangeBrokerAfterBookingDialog(
    shipment.shipmentId,
    shipment.shipmentState,
    shipment.broker,
  );

  const pickupContactChange = ViewShipmentsModifyContactAfterBookingDialog(
    shipment,
    LocationContext.Pickup,
    props.onReload,
  );
  const deliveryContactChange = ViewShipmentsModifyContactAfterBookingDialog(
    shipment,
    LocationContext.Delivery,
    props.onReload,
  );

  const pickupAddressDialog = useViewShipmentsModifyAddressAfterBookingDialog(
    shipment,
    LocationContext.Pickup,
  );
  const deliveryAddressDialog = useViewShipmentsModifyAddressAfterBookingDialog(
    shipment,
    LocationContext.Delivery,
  );

  const pickupReferenceAndNotesDialog =
    useViewShipmentsModifyReferenceAndNotesAfterBookingDialog(
      shipment,
      LocationContext.Pickup,
    );
  const deliveryReferenceAndNotesDialog =
    useViewShipmentsModifyReferenceAndNotesAfterBookingDialog(
      shipment,
      LocationContext.Delivery,
    );

  // Declared above the quote guard below so the dialogs survive it: a reload
  // can briefly leave the shipment without a selected quote while a dialog is
  // open, and an early return without the dialogs would silently unmount it
  // (a screen that owns an open Dialog must never unmount it).
  const detailsDialogs = (
    <>
      {changePickupDateDialog.dialogs}
      {pickupContactChange.dialogs}
      {deliveryContactChange.dialogs}
      {changeBrokerDialog.dialogs}
      {pickupAddressDialog.dialogs}
      {deliveryAddressDialog.dialogs}
      {pickupReferenceAndNotesDialog.dialogs}
      {deliveryReferenceAndNotesDialog.dialogs}
      <ChangeDeliveryHoursDialog
        open={changeDeliveryHoursOpen}
        onOpenChange={(open) => {
          setChangeDeliveryHoursOpen(open);
          // The legacy flow reloaded the shipment whenever the modal closed
          // (confirmed or cancelled) — keep that.
          if (!open) {
            props.onReload();
          }
        }}
        shipment={shipment}
      />
    </>
  );

  if (quote === undefined) {
    return detailsDialogs;
  }

  const lineItems = shipment.lineItems;
  const shipmentState = shipment.shipmentState;
  const pickupLocation = shipment.pickupLocation;
  const deliveryLocation = shipment.deliveryLocation;
  const pickupContact = shipment.pickupContact;
  const deliveryContact = shipment.deliveryContact;
  const pickupDate = moment(shipment.pickupDate).startOf("day");
  const actualDeliveryDate = shipment.actualDeliveryDate;
  const actualDeliveryTime = shipment.actualDeliveryTime;
  const proNumber = shipment.proNumber;
  const billOfLadingNumber = shipment.billOfLadingNumber || undefined;
  const expectedDeliveryDate = shipment.expectedDeliveryDate;
  const latestExpectedDeliveryDate =
    shipment.latestedPredictedDeliveryDate || quote.latestEstimatedDeliveryDate;
  const pickupReferenceNumber = shipment.pickupReferenceNumber;
  const deliveryReferenceNumber = shipment.deliveryReferenceNumber;
  const deliveryAmazonReferenceId = shipment.deliveryAmazonReferenceId;
  const deliveryAmazonShipmentId = shipment.deliveryAmazonShipmentId;
  const pickupBoothNumber = shipment.pickupBoothNumber;
  const deliveryBoothNumber = shipment.deliveryBoothNumber;
  const broker = shipment.broker;
  const addInsuranceToShipment = shipment.addInsuranceToShipment;
  const insuranceAmount = shipment.insuranceAmount;
  const insuranceCurrency = shipment.insuranceCurrency;
  const insuranceCommodityGroup = shipment.insuranceCommodityGroup;
  const stateOfGoods = shipment.stateOfGoods;

  const equipmentType = shipment.equipmentType;
  const exclusiveUse = shipment.exclusiveUseNeeded;
  const tarpRequired = shipment.tarpRequired;
  const linearFeet = shipment.linearFeet;
  const pickupLocationHasDock = shipment.pickupLocationHasDock;
  const pickupLocationHasForklift = shipment.pickupLocationHasForklift;
  const deliveryLocationHasDock = shipment.deliveryLocationHasDock;
  const deliveryLocationHasForklift = shipment.deliveryLocationHasForklift;

  const deliveryHours = describeHours(shipment.deliveryLocation.hours);
  const deliveryDeadline = shipment.deliveryDeadline;

  const pickupDeadline = shipment.pickupDeadline;

  const trackingLines = props.trackingInfo?.trackingLines;
  const mostRecentTrackingLine =
    trackingLines !== undefined
      ? trackingLines[trackingLines.length - 1]
      : undefined;

  return (
    <>
      {detailsDialogs}
      <ViewShipmentsDetailsSection
        pickupDeadline={pickupDeadline}
        lineItems={lineItems}
        shipmentState={shipmentState}
        pickupLocation={pickupLocation}
        deliveryLocation={deliveryLocation}
        pickupContact={pickupContact}
        deliveryContact={deliveryContact}
        pickupDate={pickupDate}
        deliveryHours={deliveryHours}
        deliveryDeadline={deliveryDeadline}
        appointmentDate={shipment.appointmentDate}
        quote={quote}
        actualDeliveryDate={actualDeliveryDate}
        actualDeliveryTime={actualDeliveryTime}
        proNumber={proNumber}
        billOfLadingNumber={billOfLadingNumber}
        expectedDeliveryDate={expectedDeliveryDate}
        latestExpectedDeliveryDate={latestExpectedDeliveryDate}
        pickupReferenceNumber={pickupReferenceNumber}
        deliveryReferenceNumber={deliveryReferenceNumber}
        deliveryAmazonReferenceId={deliveryAmazonReferenceId}
        deliveryAmazonShipmentId={deliveryAmazonShipmentId}
        pickupBoothNumber={pickupBoothNumber}
        deliveryBoothNumber={deliveryBoothNumber}
        broker={broker}
        addInsuranceToShipment={addInsuranceToShipment}
        insuranceAmount={insuranceAmount}
        insuranceCurrency={insuranceCurrency}
        insuranceCommodityGroup={insuranceCommodityGroup}
        equipmentType={equipmentType}
        exclusiveUse={exclusiveUse}
        tarpRequired={tarpRequired}
        linearFeet={linearFeet}
        stateOfGoods={stateOfGoods}
        pickupLocationHasDock={pickupLocationHasDock}
        pickupLocationHasForklift={pickupLocationHasForklift}
        deliveryLocationHasDock={deliveryLocationHasDock}
        deliveryLocationHasForklift={deliveryLocationHasForklift}
        mostRecentTrackingLine={mostRecentTrackingLine}
        onChangePickupDate={async function () {
          changePickupDateDialog.show();
        }}
        onChangeBroker={async function () {
          const changed = await changeBrokerDialog.show();
          if (changed) {
            props.onReload();
          }
        }}
        onModifyPickupAddress={async function () {
          const changed = await pickupAddressDialog.show();
          if (changed) {
            props.onReload();
          }
        }}
        onModifyDeliveryAddress={async function () {
          const changed = await deliveryAddressDialog.show();
          if (changed) {
            props.onReload();
          }
        }}
        onModifyPickupContact={async function () {
          await pickupContactChange.show();
        }}
        onModifyDeliveryContact={async function () {
          await deliveryContactChange.show();
        }}
        onModifyPickupReferenceAndNotes={async function () {
          const changed = await pickupReferenceAndNotesDialog.show();
          if (changed) {
            props.onReload();
          }
        }}
        onModifyDeliveryReferenceAndNotes={async function () {
          const changed = await deliveryReferenceAndNotesDialog.show();
          if (changed) {
            props.onReload();
          }
        }}
        onChangeDeliveryHours={
          // The carrier sets an appointment delivery's window with the
          // consignee; a customer change here would contradict it.
          shipmentIsDeliveringByAppointment(shipment)
            ? undefined
            : async function () {
                setChangeDeliveryHoursOpen(true);
              }
        }
      />
    </>
  );
}
